import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, Building2, CheckCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import lanerunnerLogoCyan from "@/assets/lanerunner-logo-cyan.png";

interface InviteInfo {
  email: string;
  role: string;
  companyName: string;
  companyType: string;
}

export default function JoinCompanyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("invite") || searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Validate invite token on mount
  useEffect(() => {
    if (!token) {
      setError("No invitation token found. Please use the link from your invitation email.");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/accept-company-invite?token=${encodeURIComponent(token)}`,
          {
            method: "GET",
            headers: {
              "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();

        if (!response.ok) {
          setError(data.error || "Invalid or expired invitation.");
          setLoading(false);
          return;
        }

        setInviteInfo(data);
      } catch (err) {
        setError("Failed to validate invitation. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("accept-company-invite", {
        body: { token, password, firstName, lastName, phone },
      });

      if (fnError) throw fnError;
      if (data?.error) {
        setError(data.error);
        setSubmitting(false);
        return;
      }

      setSuccess(true);

      // Auto-login after 2 seconds
      setTimeout(() => {
        navigate("/auth?tab=login");
      }, 3000);
    } catch (err: any) {
      setError(err.message || "Failed to create account. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Welcome to the team!</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Your account has been created and you've been added to <strong>{inviteInfo?.companyName}</strong>.
          </p>
          <p className="text-sm text-muted-foreground">Redirecting you to login...</p>
          <Button className="mt-4" onClick={() => navigate("/auth?tab=login")}>
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  // Error state (no valid invite)
  if (!inviteInfo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Invalid Invitation</h2>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button variant="outline" onClick={() => navigate("/")}>
            Go to Homepage
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left brand panel */}
      <div className="hidden md:flex md:w-[35%] bg-[#0c1220] flex-col justify-between p-10 text-white">
        <div>
          <div className="flex items-center gap-2 mb-12">
            <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-8" />
          </div>
          <div className="w-10 h-10 bg-white/10 border border-white/20 rounded-lg flex items-center justify-center mb-6">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Join {inviteInfo.companyName}</h2>
          <p className="text-white/50 text-sm mb-8">
            You've been invited to join as a {inviteInfo.role}. Complete your profile below to get started.
          </p>
          <div className="space-y-3">
            {[
              "Set up your own secure login",
              "Access your company's freight tools",
              "No credit card required",
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle className="w-4 h-4 text-[hsl(var(--brand-teal))]" />
                <span className="text-white/60 text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="mb-8">
            <div className="md:hidden mb-6">
              <img src={lanerunnerLogoCyan} alt="LaneRunner" className="h-8" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-1">Create Your Account</h1>
            <p className="text-muted-foreground">
              Joining <strong>{inviteInfo.companyName}</strong> as a <strong className="capitalize">{inviteInfo.role}</strong>
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email (read-only) */}
            <div>
              <Label>Email</Label>
              <Input value={inviteInfo.email} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground mt-1">This is the email your invitation was sent to</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating Account...</>
              ) : (
                "Create Account & Join Team"
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              By creating an account, you agree to our{" "}
              <a href="/terms" className="text-primary underline">Terms</a> and{" "}
              <a href="/privacy" className="text-primary underline">Privacy Policy</a>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
